import { useState, useEffect, useCallback } from 'react';
import { Send, RefreshCw, Loader2, Mail, Clock, CheckCircle, XCircle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const DeliveriesPanel = ({ gallery, galleryId, axiosConfig, backendUrl }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backendUrl}/api/admin/gallery/${galleryId}/deliveries`,
        axiosConfig
      );
      setDeliveries(response.data);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      toast.error('Failed to load delivery history');
    } finally {
      setLoading(false);
    }
  }, [galleryId, backendUrl, axiosConfig]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handleResend = async (deliveryId) => {
    setResending(deliveryId);
    try {
      await axios.post(
        `${backendUrl}/api/admin/gallery/${galleryId}/deliveries/${deliveryId}/resend`,
        {},
        axiosConfig
      );
      toast.success('Email resent successfully');
      fetchDeliveries();
    } catch (error) {
      console.error('Error resending:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResending(null);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'sent') {
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Sent
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
        <XCircle className="w-3 h-3 mr-1" />
        Failed
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const configs = {
      invitation: { label: 'Invitation', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      external: { label: 'External', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' }
    };
    const config = configs[type] || configs.invitation;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#BF212F] rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20">
            <Send className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">Deliveries</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Email delivery history for this gallery
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchDeliveries}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : deliveries.length === 0 ? (
        /* Empty State */
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-300 mb-2">
              No Deliveries Yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
              When you send gallery invitation emails via the Share Links panel, delivery records will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Deliveries Table */
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Recipient</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Sent By</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Sent At</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600 dark:text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {deliveries.map((delivery) => (
                  <tr key={delivery.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-800 dark:text-slate-200 truncate max-w-[200px]">
                          {delivery.recipient_email}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getTypeBadge(delivery.type)}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(delivery.status)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                      {delivery.sent_by || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatDate(delivery.sent_at)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResend(delivery.id)}
                        disabled={resending === delivery.id}
                        title="Resend this email"
                      >
                        {resending === delivery.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCw className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Info Card */}
      {deliveries.length > 0 && (
        <Card className="mt-4 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1 text-sm">
            About Deliveries
          </h4>
          <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
            <li>Delivery records are created when gallery invitation emails are sent</li>
            <li>Click the resend button to re-send an email to the same recipient</li>
          </ul>
        </Card>
      )}
    </div>
  );
};

export default DeliveriesPanel;
